import React, { useState, useEffect } from 'react';
import { Box, TextField, MenuItem, Button, Paper, Autocomplete, CircularProgress } from '@mui/material';
import axiosInstance from '../api/axiosInstance';
const INDUSTRY_OPTIONS = ['IT', 'Healthcare', 'Finance', 'Manufacturing', 'Retail'];

const EXPERIENCE_OPTIONS = [
  { value: 'entry', label: 'Entry Level' },
  { value: 'mid', label: 'Mid Level' },
  { value: 'senior', label: 'Senior' },
  { value: 'lead', label: 'Lead' },
];

const POSTED_WITHIN_OPTIONS = [
  { value: '1', label: 'Last 24 hours' },
  { value: '3', label: 'Last 3 days' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
];

const emptyFilters = {
  jobTitle: '',
  location: '',
  industry: '',
  experience: '',
  postedWithinDays: '30',
};

const COUNTRIES_CACHE_KEY = 'hirepeaks_countries_cache';
const COUNTRIES_CACHE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function getCachedCountries() {
  try {
    const raw = localStorage.getItem(COUNTRIES_CACHE_KEY);
    if (!raw) return null;
    const { data, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > COUNTRIES_CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function setCachedCountries(data) {
  try {
    localStorage.setItem(COUNTRIES_CACHE_KEY, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // ignore storage failures
  }
}

const FIELD_HEIGHT = 34;
const FIELD_FONT_SIZE = 13;

// Applied identically to every field so height/font-size can never drift between them again.
const fieldSx = {
  '& .MuiInputBase-root': { height: FIELD_HEIGHT },
  '& .MuiInputBase-input': { fontSize: FIELD_FONT_SIZE },
  '& .MuiInputLabel-root': { fontSize: FIELD_FONT_SIZE },
};

export default function FilterBar({ onApply }) {
  const [filters, setFilters] = useState(emptyFilters);
  const [countries, setCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(true);
  const [countriesError, setCountriesError] = useState('');

  useEffect(() => {
    async function loadCountries() {
      const cached = getCachedCountries();
      if (cached) {
        setCountries(cached);
        setCountriesLoading(false);
        return;
      }

      try {
         const response = await axiosInstance.get('/countries');
         const formatted = response.data;
        setCountries(formatted);
        setCachedCountries(formatted);
      } catch (err) {
        setCountriesError('Could not load country list.');
      } finally {
        setCountriesLoading(false);
      }
    }
    loadCountries();
  }, []);

  const handleChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleCountryChange = (event, selectedCountry) => {
    setFilters((prev) => ({ ...prev, location: selectedCountry ? selectedCountry.code : '' }));
  };

  const handleApply = () => onApply(filters);
  const handleReset = () => {
    setFilters(emptyFilters);
    onApply(emptyFilters);
  };

  const selectedCountryObj = countries.find((c) => c.code === filters.location) || null;

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, mb: 3, borderRadius: 2, display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}
    >
      <TextField
        size="small"
        label="Job Title"
        value={filters.jobTitle}
        onChange={handleChange('jobTitle')}
        sx={{ flex: '1 1 160px', minWidth: 140, ...fieldSx }}
      />

      <Autocomplete
        size="small"
        options={countries}
        loading={countriesLoading}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        value={selectedCountryObj}
        onChange={handleCountryChange}
        sx={{ flex: '1 1 160px', minWidth: 160, ...fieldSx }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Location"
            placeholder="Search country"
            error={!!countriesError}
            helperText={countriesError || undefined}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps?.input,
                endAdornment: (
                  <>
                    {countriesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.slotProps?.input?.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
      />

      <TextField
        select
        size="small"
        label="Industry"
        value={filters.industry}
        onChange={handleChange('industry')}
        sx={{ flex: '1 1 140px', minWidth: 130, ...fieldSx }}
      >
        <MenuItem value="">All</MenuItem>
        {INDUSTRY_OPTIONS.map((opt) => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
      </TextField>

      <TextField
        select
        size="small"
        label="Experience"
        value={filters.experience}
        onChange={handleChange('experience')}
        sx={{ flex: '1 1 140px', minWidth: 130, ...fieldSx }}
      >
        <MenuItem value="">All</MenuItem>
        {EXPERIENCE_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
      </TextField>

      <TextField
        select
        size="small"
        label="Posted"
        value={filters.postedWithinDays}
        onChange={handleChange('postedWithinDays')}
        sx={{ flex: '1 1 140px', minWidth: 140, ...fieldSx }}
      >
        {POSTED_WITHIN_OPTIONS.map((opt) => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
      </TextField>

      <Box sx={{ display: 'flex', gap: 1.5, flexShrink: 0 }}>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleReset}
          sx={{ height: FIELD_HEIGHT, fontSize: FIELD_FONT_SIZE }}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          sx={{ height: FIELD_HEIGHT, fontSize: FIELD_FONT_SIZE }}
        >
          Apply filters
        </Button>
      </Box>
    </Paper>
  );
}